import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    targets: Array,
    skipOwn: { type: Boolean, default: false },
    selfId: String,
    sosEnabled: { type: Boolean, default: true },
    allowedUserIds: Array,
    appOwner: { type: Boolean, default: false },
    tone: { type: String, default: "notification" }
  }

  connect() {
    this.audioReadyStorageKey = "sound_alert_audio_ready"
    this.audioReady = this.restoreAudioReadyFlag()
    if (!this.audioReady && navigator.userActivation?.hasBeenActive) {
      this.audioReady = true
      this.persistAudioReadyFlag()
    }
    this.audioCtx = null
    this.lastBadgeCount = this.readBadgeCountFromDom()
    this.lastSosSoundAt = 0
    this.sosAlarmDedupMs = 9000
    this.systemNotificationIntervalMs = 10000
    this.suppressNextBadgeSoundUntil = 0
    this.sosAlarms = new Map()
    this.sosAlarmTimers = new Map()
    this.sosAlarmRepeatMs = 9000
    this.sosActiveStorageKey = "sos_alert_active_alarms"
    this.sosMutedStorageKey = "sos_alert_muted_ids"
    this.sosMutedAlertIds = this.restoreMutedSosAlerts()
    this.onFirstInteraction = this.onFirstInteraction.bind(this)
    this.onBeforeStreamRender = this.onBeforeStreamRender.bind(this)
    this.onSosAlert = this.onSosAlert.bind(this)
    this.onVisibilityChange = this.onVisibilityChange.bind(this)
    this.onStopLocalSosAlert = this.onStopLocalSosAlert.bind(this)

    document.addEventListener("pointerdown", this.onFirstInteraction, { once: true })
    document.addEventListener("touchstart", this.onFirstInteraction, { once: true, passive: true })
    document.addEventListener("touchend", this.onFirstInteraction, { once: true, passive: true })
    document.addEventListener("keydown", this.onFirstInteraction, { once: true })
    document.addEventListener("turbo:before-stream-render", this.onBeforeStreamRender)
    if (this.sosEnabledValue) {
      document.addEventListener("sos:alert", this.onSosAlert)
      document.addEventListener("sos:stop-local-alert", this.onStopLocalSosAlert)
      this.restoreActiveSosAlarms()
    }
    document.addEventListener("visibilitychange", this.onVisibilityChange)

    if (this.audioReady && navigator.userActivation?.hasBeenActive) this.ensureAudioContextReady()
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.onBeforeStreamRender)
    if (this.sosEnabledValue) {
      document.removeEventListener("sos:alert", this.onSosAlert)
      document.removeEventListener("sos:stop-local-alert", this.onStopLocalSosAlert)
      this.pauseAllSosAlarmsForNavigation()
      this.clearSosAlarmTimers()
    }
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
  }

  async onFirstInteraction() {
    this.audioReady = true
    this.persistAudioReadyFlag()
    await this.ensureAudioContextReady()
  }

  async onVisibilityChange() {
    if (document.visibilityState === "visible" && this.audioReady && navigator.userActivation?.hasBeenActive) {
      await this.ensureAudioContextReady()
    }
  }

  onBeforeStreamRender(event) {
    const stream = event?.target
    if (!stream || stream.tagName !== "TURBO-STREAM") return

    const action = (stream.getAttribute("action") || "").toLowerCase()
    const target = stream.getAttribute("target") || ""

    this.maybeTriggerSosFromNotificationStream(stream, action, target)

    // Notification badge updates often come as replace streams.
    if (action === "replace" && target === "notifications_badge") {
      const nextCount = this.readBadgeCountFromStream(stream)
      const suppressed = Date.now() < this.suppressNextBadgeSoundUntil
      if (nextCount > this.lastBadgeCount && !suppressed) this.playBeep()
      this.lastBadgeCount = nextCount
      return
    }

    if (action !== "append" && action !== "prepend") return
    if (!this.watchesTarget(target)) return
    if (this.silentNotificationStream(stream, target)) return

    if (this.skipOwnValue && this.isOwnMessage(stream)) return
    this.playBeep()
  }

  watchesTarget(target) {
    const watched = this.targetsValue.length ? this.targetsValue : ["messages"]
    return watched.some((entry) => {
      if (entry.endsWith("*")) {
        return target.startsWith(entry.slice(0, -1))
      }
      return target === entry
    })
  }

  isOwnMessage(stream) {
    const content = stream.templateElement?.content
    if (!content) return false

    const firstNode = content.firstElementChild
    if (!firstNode) return false

    return firstNode.className.includes("justify-end")
  }

  readBadgeCountFromDom() {
    const badge = document.getElementById("notifications_badge")
    return this.extractCount(badge?.textContent || "")
  }

  readBadgeCountFromStream(stream) {
    const content = stream.templateElement?.content
    if (!content) return this.lastBadgeCount || 0

    return this.extractCount(content.textContent || "")
  }

  extractCount(text) {
    const m = text.match(/\d+/)
    return m ? parseInt(m[0], 10) : 0
  }


  maybeTriggerSosFromNotificationStream(stream, action, target) {
    if (action !== "append" && action !== "prepend") return
    if (target !== "notifications_list") return

    const content = stream.templateElement?.content
    const firstNode = content?.firstElementChild
    if (!firstNode) return

    const kind = firstNode.getAttribute("data-notification-kind") || ""
    if (kind !== "sos_alert") return

    const sosAlertId = this.normalizeId(firstNode.getAttribute("data-notification-sos-alert-id"))
    const userId = this.normalizeId(firstNode.getAttribute("data-notification-actor-id"))
    const name = (firstNode.getAttribute("data-notification-actor-name") || "A friend").trim() || "A friend"
    const redirectPath = (firstNode.getAttribute("data-notification-link") || "/notifications").trim() || "/notifications"
    if (!sosAlertId && !userId) return
    if (this.isSosMuted({ sosAlertId })) return

    document.dispatchEvent(new CustomEvent("sos:alert", {
      detail: {
        source: "notification-stream",
        userId,
        name,
        sosAlertId,
        redirectPath
      }
    }))
  }

  silentNotificationStream(stream, target) {
    if (target !== "notifications_list") return false

    const content = stream.templateElement?.content
    const firstNode = content?.firstElementChild
    if (!firstNode) return false

    const kind = firstNode.getAttribute("data-notification-kind") || ""
    const silentKinds = ["global_live_started", "global_live_finished"]
    if (!silentKinds.includes(kind)) return false

    this.suppressNextBadgeSoundUntil = Date.now() + 1500
    return true
  }

  onSosAlert(event) {
    if (this.isOwnSosEvent(event)) return

    const detail = event?.detail || {}
    if (this.isSosMuted(detail)) return
    if (!this.soundAllowedForSos(detail)) return
    if (!this.audioReady && navigator.userActivation?.hasBeenActive) {
      this.audioReady = true
      this.persistAudioReadyFlag()
      this.ensureAudioContextReady()
    }
    const alarmKey = this.alarmKey(detail)
    if (!alarmKey) return

    const existing = this.sosAlarms.get(alarmKey) || {}
    const state = {
      key: alarmKey,
      userId: this.normalizeId(detail.userId || detail.user_id),
      alertId: this.normalizeId(detail.sosAlertId || detail.sos_alert_id),
      name: (detail.name || existing.name || "A friend").toString(),
      redirectPath: (detail.redirectPath || detail.redirect_path || existing.redirectPath || "/notifications").toString(),
      authorityPath: (detail.authorityPath || detail.authority_path || existing.authorityPath || "/government_agencies").toString(),
      lastSeenAt: Date.now()
    }
    this.sosAlarms.set(alarmKey, state)

    const shouldTrigger = !existing.lastSeenAt || (Date.now() - Number(existing.lastSeenAt || 0)) >= this.sosAlarmDedupMs
    if (shouldTrigger) this.triggerSosAlarm(state)
    this.startRepeatingSosAlarm(state)

    this.persistActiveSosAlarms()
  }

  triggerSosAlarm(state) {
    const now = Date.now()
    if (now - this.lastSosSoundAt < 1200) return
    this.lastSosSoundAt = now
    this.showSosToast({ name: state.name, sosAlertId: state.alertId, userId: state.userId, redirectPath: state.redirectPath, authorityPath: state.authorityPath })
    this.flashSosOverlay()
    this.playSosTone()
    this.vibrateSosAlert()
    this.showSystemSosNotification(state)
  }

  async showSystemSosNotification(state) {
    try {
      if (!("Notification" in window)) return
      if (Notification.permission !== "granted") return
      if (document.visibilityState === "visible" && document.hasFocus()) return

      const now = Date.now()
      if (state.lastSystemNotificationAt && (now - state.lastSystemNotificationAt) < this.systemNotificationIntervalMs) return
      state.lastSystemNotificationAt = now
      this.sosAlarms.set(state.key, state)

      const title = `${state.name || "A friend"} needs help now`
      const options = {
        body: "SOS alert received in Go Safe 360.",
        tag: state.alertId ? `sos-alert-${state.alertId}` : `sos-user-${state.userId || "unknown"}`,
        renotify: true,
        requireInteraction: true,
        silent: false,
        data: {
          url: state.redirectPath || "/notifications",
          sosAlertId: state.alertId || "",
          userId: state.userId || ""
        }
      }

      if (navigator.serviceWorker?.ready) {
        const registration = await navigator.serviceWorker.ready
        if (registration?.showNotification) {
          await registration.showNotification(title, options)
          return
        }
      }

      const notification = new Notification(title, options)
      notification.onclick = () => {
        notification.close()
        if (window.Turbo && typeof window.Turbo.visit === "function") {
          document.dispatchEvent(new CustomEvent("sos:stop-local-alert", { detail: { sosAlertId: state.alertId, userId: state.userId } }))
          window.Turbo.visit(state.redirectPath || "/notifications")
        } else {
          document.dispatchEvent(new CustomEvent("sos:stop-local-alert", { detail: { sosAlertId: state.alertId, userId: state.userId } }))
          window.location.href = state.redirectPath || "/notifications"
        }
      }
    } catch (_error) {
      // Ignore notification failures and keep in-page SOS UX running.
    }
  }

  onStopLocalSosAlert(event) {
    const detail = event?.detail || {}
    const alertId = this.normalizeId(detail.sosAlertId || detail.sos_alert_id)
    const userId = this.normalizeId(detail.userId || detail.user_id)
    const muteAll = detail.muteAll || detail.mute_all

    if (muteAll) {
      this.clearAllSosAlarms()
      this.persistActiveSosAlarms()
      return
    }

    if (alertId) {
      this.muteSosAlertId(alertId)
      this.sosAlarms.forEach((state, key) => {
        if (state.alertId && state.alertId === alertId) this.stopSosAlarm(key)
      })
    }

    if (userId) {
      this.sosAlarms.forEach((state, key) => {
        if (state.userId && state.userId === userId) this.stopSosAlarm(key)
      })
    }

    this.persistActiveSosAlarms()
  }

  stopSosAlarm(alarmKey) {
    const state = this.sosAlarms.get(alarmKey)
    if (!state) return
    this.stopRepeatingSosAlarm(alarmKey)
    this.sosAlarms.delete(alarmKey)
    this.persistActiveSosAlarms()
  }

  clearAllSosAlarms() {
    this.clearSosAlarmTimers()
    this.sosAlarms.clear()
    this.persistActiveSosAlarms()
  }

  pauseAllSosAlarmsForNavigation() {
    this.persistActiveSosAlarms()
  }

  alarmKey(detail = {}) {
    const alertId = this.normalizeId(detail.sosAlertId || detail.sos_alert_id)
    if (alertId) return `alert:${alertId}`

    const userId = this.normalizeId(detail.userId || detail.user_id)
    if (userId) return `user:${userId}`

    return ""
  }

  isSosMuted(detail = {}) {
    const alertId = this.normalizeId(detail.sosAlertId || detail.sos_alert_id)
    return !!alertId && this.sosMutedAlertIds.has(alertId)
  }

  muteSosAlertId(alertId) {
    if (!alertId) return
    this.sosMutedAlertIds.add(alertId)
    this.persistMutedSosAlerts()
  }

  persistMutedSosAlerts() {
    try {
      window.sessionStorage.setItem(this.sosMutedStorageKey, JSON.stringify(Array.from(this.sosMutedAlertIds)))
    } catch (_e) {
      // Ignore storage errors.
    }
  }

  restoreMutedSosAlerts() {
    try {
      const raw = window.sessionStorage.getItem(this.sosMutedStorageKey)
      const arr = raw ? JSON.parse(raw) : []
      return new Set(Array.isArray(arr) ? arr.map((v) => String(v)) : [])
    } catch (_e) {
      return new Set()
    }
  }

  persistActiveSosAlarms() {
    try {
      const payload = Array.from(this.sosAlarms.values()).map((state) => ({
        key: state.key,
        userId: state.userId || "",
        alertId: state.alertId || "",
        name: state.name || "A friend",
        redirectPath: state.redirectPath || "/notifications",
        authorityPath: state.authorityPath || "/government_agencies",
        lastSeenAt: Number(state.lastSeenAt || Date.now())
      }))
      window.sessionStorage.setItem(this.sosActiveStorageKey, JSON.stringify(payload))
    } catch (_e) {
      // Ignore storage errors.
    }
  }

  restoreActiveSosAlarms() {
    let rows = []
    try {
      const raw = window.sessionStorage.getItem(this.sosActiveStorageKey)
      rows = raw ? JSON.parse(raw) : []
    } catch (_e) {
      rows = []
    }

    if (!Array.isArray(rows)) return

    rows.forEach((row) => {
      const alertId = this.normalizeId(row?.alertId)
      const userId = this.normalizeId(row?.userId)
      const key = this.normalizeId(row?.key) || this.alarmKey({ sos_alert_id: alertId, user_id: userId })
      if (!key) return
      if (alertId && this.sosMutedAlertIds.has(alertId)) return

      const state = {
        key,
        userId,
        alertId,
        name: (row?.name || "A friend").toString(),
        redirectPath: (row?.redirectPath || "/notifications").toString(),
        authorityPath: (row?.authorityPath || "/government_agencies").toString(),
        lastSeenAt: Number(row?.lastSeenAt || Date.now()),
      }

      this.sosAlarms.set(key, state)
      this.startRepeatingSosAlarm(state)
    })

    this.persistActiveSosAlarms()
  }

  startRepeatingSosAlarm(state) {
    if (!state?.key) return
    if (this.sosAlarmTimers.has(state.key)) return

    const timerId = window.setInterval(() => {
      const current = this.sosAlarms.get(state.key)
      if (!current) {
        this.stopRepeatingSosAlarm(state.key)
        return
      }
      if (current.alertId && this.sosMutedAlertIds.has(current.alertId)) {
        this.stopSosAlarm(state.key)
        return
      }

      this.triggerSosAlarm(current)
    }, this.sosAlarmRepeatMs)

    this.sosAlarmTimers.set(state.key, timerId)
  }

  stopRepeatingSosAlarm(alarmKey) {
    const timerId = this.sosAlarmTimers.get(alarmKey)
    if (timerId) window.clearInterval(timerId)
    this.sosAlarmTimers.delete(alarmKey)
  }

  clearSosAlarmTimers() {
    this.sosAlarmTimers.forEach((timerId) => window.clearInterval(timerId))
    this.sosAlarmTimers.clear()
  }

  flashSosOverlay() {
    const id = "global-sos-flash-overlay"
    let overlay = document.getElementById(id)
    if (!overlay) {
      overlay = document.createElement("div")
      overlay.id = id
      overlay.style.position = "fixed"
      overlay.style.inset = "0"
      overlay.style.background = "rgba(220,38,38,0.2)"
      overlay.style.pointerEvents = "none"
      overlay.style.zIndex = "9998"
      overlay.style.opacity = "0"
      overlay.style.transition = "opacity 180ms ease-out"
      document.body.appendChild(overlay)
    }

    overlay.style.opacity = "1"
    window.setTimeout(() => {
      overlay.style.opacity = "0"
    }, 420)
  }

  showSosToast(detail = {}) {
    const existing = document.getElementById("global-sos-toast")
    if (existing) existing.remove()

    const toast = document.createElement("div")
    toast.id = "global-sos-toast"
    toast.setAttribute("role", "alert")
    toast.style.position = "fixed"
    toast.style.right = "16px"
    toast.style.bottom = "16px"
    toast.style.zIndex = "9999"
    toast.style.maxWidth = "360px"
    toast.style.padding = "12px 14px"
    toast.style.borderRadius = "12px"
    toast.style.background = "#dc2626"
    toast.style.color = "white"
    toast.style.boxShadow = "0 10px 30px rgba(0,0,0,0.25)"
    toast.style.fontSize = "14px"
    toast.style.lineHeight = "1.3"
    const name = detail?.name ? String(detail.name) : "A friend"
    const title = document.createElement("div")
    title.textContent = `SOS ALERT: ${name} needs help now.`
    title.style.fontWeight = "800"

    const actions = document.createElement("div")
    actions.style.display = "flex"
    actions.style.flexWrap = "wrap"
    actions.style.gap = "8px"
    actions.style.marginTop = "10px"

    const respond = document.createElement("button")
    respond.type = "button"
    respond.textContent = "Respond"
    respond.style.border = "1px solid rgba(255,255,255,.55)"
    respond.style.borderRadius = "9999px"
    respond.style.padding = "6px 10px"
    respond.style.fontWeight = "800"
    respond.style.background = "white"
    respond.style.color = "#991b1b"

    const authority = document.createElement("button")
    authority.type = "button"
    authority.textContent = "Report to authority"
    authority.style.border = "1px solid rgba(255,255,255,.55)"
    authority.style.borderRadius = "9999px"
    authority.style.padding = "6px 10px"
    authority.style.fontWeight = "800"
    authority.style.background = "rgba(255,255,255,.15)"
    authority.style.color = "white"

    const mute = document.createElement("button")
    mute.type = "button"
    mute.textContent = "Mute/stop for me"
    mute.style.border = "1px solid rgba(255,255,255,.55)"
    mute.style.borderRadius = "9999px"
    mute.style.padding = "6px 10px"
    mute.style.fontWeight = "800"
    mute.style.background = "rgba(255,255,255,.15)"
    mute.style.color = "white"

    actions.appendChild(respond)
    actions.appendChild(authority)
    actions.appendChild(mute)
    toast.appendChild(title)
    toast.appendChild(actions)
    toast.style.cursor = "pointer"

    const visitPath = (path) => {
      const redirectPath = path || "/notifications"
      if (window.Turbo && typeof window.Turbo.visit === "function") {
        window.Turbo.visit(redirectPath)
      } else {
        window.location.href = redirectPath
      }
    }

    respond.addEventListener("click", (event) => {
      event.stopPropagation()
      visitPath(detail.redirectPath || "/notifications")
    })

    authority.addEventListener("click", (event) => {
      event.stopPropagation()
      visitPath(detail.authorityPath || "/government_agencies")
    })

    mute.addEventListener("click", (event) => {
      event.stopPropagation()
      document.dispatchEvent(new CustomEvent("sos:stop-local-alert", {
        detail: {
          sosAlertId: detail.sosAlertId,
          userId: detail.userId
        }
      }))
      if (toast.parentNode) toast.parentNode.removeChild(toast)
    })

    toast.addEventListener("click", () => visitPath(detail.redirectPath || "/notifications"))
    document.body.appendChild(toast)

    window.setTimeout(() => {
      if (toast.parentNode) toast.parentNode.removeChild(toast)
    }, 9000)
  }

  isOwnSosEvent(event) {
    const ownId = this.normalizeId(this.selfIdValue)
    if (!ownId) return false

    const sourceId = this.normalizeId(event?.detail?.userId)
    return !!sourceId && sourceId === ownId
  }

  soundAllowedForSos(detail = {}) {
    if (this.appOwnerValue === true) return true

    const userId = this.normalizeId(detail.userId || detail.user_id)
    if (!userId) return false

    const allowed = Array.isArray(this.allowedUserIdsValue) ? this.allowedUserIdsValue : []
    return allowed.map(String).includes(userId)
  }

  normalizeId(value) {
    if (value === null || value === undefined) return ""
    return String(value).trim()
  }

  async playBeep() {
    if (!this.audioReady) return

    try {
      const ctx = await this.ensureAudioContextReady()
      if (!ctx) return
      const gainNode = ctx.createGain()
      const tones = this.alertToneSequence()
      const step = 0.13

      gainNode.gain.setValueAtTime(0.0001, ctx.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + 0.01)
      gainNode.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + Math.max(0.18, tones.length * step + 0.06))
      gainNode.connect(ctx.destination)

      tones.forEach((frequency, index) => {
        const start = ctx.currentTime + index * step
        const oscillator = ctx.createOscillator()
        oscillator.type = "sine"
        oscillator.frequency.setValueAtTime(frequency, start)
        oscillator.connect(gainNode)
        oscillator.start(start)
        oscillator.stop(start + 0.11)
      })
    } catch (_error) {
      // Ignore audio failures; UX should remain functional.
    }
  }

  alertToneSequence() {
    switch ((this.toneValue || "notification").toString()) {
      case "chat":
        return [880]
      case "group":
        return [660, 880]
      case "call":
        return [880, 1100, 880]
      case "global":
        return [740, 988]
      default:
        return [1040]
    }
  }

  async playSosTone() {
    if (!this.audioReady) return

    try {
      const ctx = await this.ensureAudioContextReady()
      if (!ctx) return
      const gainNode = ctx.createGain()
      gainNode.connect(ctx.destination)

      const totalSeconds = 4.0
      const step = 0.25
      const tones = [1100, 900]
      const loops = Math.floor(totalSeconds / step)

      gainNode.gain.setValueAtTime(0.0001, ctx.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.14, ctx.currentTime + 0.02)

      for (let i = 0; i < loops; i += 1) {
        const start = ctx.currentTime + i * step
        const osc = ctx.createOscillator()
        osc.type = "square"
        osc.frequency.setValueAtTime(tones[i % tones.length], start)
        osc.connect(gainNode)
        osc.start(start)
        osc.stop(start + 0.14)
      }

      gainNode.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + totalSeconds)
    } catch (_error) {
      // Ignore audio failures; UX should remain functional.
    }
  }

  vibrateSosAlert() {
    try {
      if (typeof window.navigator?.vibrate === "function") {
        window.navigator.vibrate([250, 120, 250, 120, 250])
      }
    } catch (_error) {
      // Ignore vibration failures.
    }
  }

  async ensureAudioContextReady() {
    const AudioCtx = window.AudioContext || window.webkitAudioContext
    if (!AudioCtx) return null

    if (!this.audioCtx) this.audioCtx = new AudioCtx()
    if (this.audioCtx.state === "suspended") {
      try {
        await this.audioCtx.resume()
      } catch (_error) {
        return null
      }
    }

    if (this.audioCtx.state !== "running") return null

    // Prime output path for iOS Safari.
    try {
      const osc = this.audioCtx.createOscillator()
      const gain = this.audioCtx.createGain()
      gain.gain.setValueAtTime(0.00001, this.audioCtx.currentTime)
      osc.frequency.setValueAtTime(440, this.audioCtx.currentTime)
      osc.connect(gain)
      gain.connect(this.audioCtx.destination)
      osc.start()
      osc.stop(this.audioCtx.currentTime + 0.01)
    } catch (_e) {
      // noop
    }

    return this.audioCtx
  }

  persistAudioReadyFlag() {
    try {
      window.sessionStorage.setItem(this.audioReadyStorageKey, "1")
    } catch (_e) {
      // noop
    }
  }

  restoreAudioReadyFlag() {
    try {
      return window.sessionStorage.getItem(this.audioReadyStorageKey) === "1"
    } catch (_e) {
      return false
    }
  }
}


